import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CookieService } from '@valhalla/core';
import { IAsmxDto, IUser, IUserProfile, IUserStatus } from '@valhalla/data/entities';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, exhaustMap, ignoreElements, map, shareReplay, startWith } from 'rxjs/operators';

import { IApiResponse } from '../api-response';
import ApiVersion from '../api-versions';
import { DataHttpCommonService } from '../data-http-common.service';
import { EndpointUrlConfig } from '../endpoint.config';
import { UsersDataHttpService } from './abstract';
import {
	ICowokersCriteria,
	IGetUserContactsCriteria,
	IGetUserSessionInfoResult,
	IGetUserSettingsResult,
	IGetUserStatusCriteria,
	IRecipientResolve,
	IRecipientResponse,
	IUserShortInfo,
	MtfAreaView,
} from './types';

import type { IEndpointUrlConfig } from '../endpoint.config';
@Injectable()
export class UsersDataHttpServiceImpl implements UsersDataHttpService {
	constructor(
		@Inject(EndpointUrlConfig) readonly config: IEndpointUrlConfig,
		readonly http: HttpClient,
		readonly common: DataHttpCommonService,
		readonly cookies: CookieService
	) {}

	domParser = new DOMParser();

	quickRepliesReq$ = new Subject();
	quickReplies$ = this.quickRepliesReq$.pipe(
		startWith(0),
		exhaustMap(() => {
			const url = this.common.getApiUrl(`users/current/quickreplies`, ApiVersion.v10);
			return this.http.get<string[]>(url);
		}),
		shareReplay({ refCount: false, bufferSize: 1 })
	);

	getStatus(criteria: IGetUserStatusCriteria): Observable<IUserStatus[]> {
		if (criteria.userIds.length === 0) {
			return of().pipe(ignoreElements());
		}
		const url = this.common.getApiUrl(`users/status`);
		return this.http.post<IUserStatus[]>(url, criteria.userIds).pipe(
			catchError(err => {
				if (err instanceof HttpErrorResponse) {
					if (err.status === 404) {
						return of().pipe(ignoreElements());
					}
				}
				return throwError(err);
			})
		);
	}

	getMembersInGroup(groupId: number): Observable<{ id: number; name: string; type: string }[]> {
		const url = this.common.getEndpointUrl('/TCWeb.asmx/GetGroupCard');
		return this.http.post<any>(url, { groupID: groupId, rootPath: '.' }).pipe(
			map(response => response.d),
			map(html => this.parseUsersFromHTML(html)),
			catchError(err => {
				console.error(err);
				return throwError(err);
			})
		);
	}

	getMembersGroup(groupId: number): Observable<any> {
		const url = this.common.getApiUrl(`group/group-card`);
		return this.http.get<IApiResponse<any>>(url, { params: { groupId } }).pipe(map(res => res.data));
	}

	parseUsersFromHTML(html: string): Array<{ id: number; name: string; type: string }> {
		if (!html) return [];

		const document = this.domParser.parseFromString(html, 'text/html');
		const userNodes = document.body.querySelectorAll<HTMLElement>('.user');
		const users: Array<{ id: number; name: string; type: string }> = Array.from(userNodes || []).map(node => {
			return {
				id: parseInt(node.dataset.userid, 10),
				name: node.textContent,
				type: 'user',
			};
		});

		return users;
	}

	getSettings(userId: number): Observable<IGetUserSettingsResult> {
		const url = this.common.getApiUrl(`user/settings`);

		return this.http
			.get<IApiResponse<IGetUserSettingsResult>>(url, {
				params: {
					userId: String(userId),
				},
			})
			.pipe(map(res => this.applyUserSettingsFromCookies(res.data)));
	}

	getUiSettings(userId: number): Observable<any> {
		const url = this.common.getApiUrl(`user/${userId}/ui-settings`);

		return this.http.get<any>(url);
	}

	postUiSettings(userId: number, body): Observable<void> {
		const url = this.common.getApiUrl(`user/${userId}/ui-settings`);

		return this.http.post<void>(url, body);
	}

	getUserInfo(userId: number): Observable<IUser> {
		const url = this.common.getApiUrl(`/user/${userId}/info`);
		return this.http.get<IApiResponse<IUser>>(url).pipe(map(res => res.data));
	}

	getUserProfile(userId: number): Observable<IUserProfile> {
		const url = this.common.getApiUrl(`/user/${userId}/profile`);
		return this.http.get<IApiResponse<IUserProfile>>(url).pipe(map(res => res.data));
	}

	getSessionUserInfo(): Observable<IGetUserSessionInfoResult> {
		const url = this.common.getApiUrl(`auth/info`);
		return this.http.get<IApiResponse<IGetUserSessionInfoResult>>(url).pipe(map(res => res.data));
	}

	getContacts(criteria?: Partial<IGetUserContactsCriteria>): Observable<IUser[]> {
		const body = {
			query: String((criteria && criteria.userName) || '          '),
		};
		const url = this.common.getEndpointUrl('/iOSClientServices/DataService.asmx/SearchContacts');
		return this.http.post<IAsmxDto<IUser[]>>(url, body).pipe(
			map(result => {
				if (!result.d) return [];
				return result.d.map(i => {
					i = this.common.normalizeResponseDto(i);
					i.absences = (i.absences || []).map(a => this.common.normalizeResponseDto(a));
					return i;
				});
			})
		);
	}

	getContactsWithGroups(criteria?: Partial<IRecipientResolve>): Observable<IRecipientResponse> {
		const url = this.common.getApiUrl(`recipient/resolve`);
		return this.http.post<IApiResponse<IRecipientResponse>>(url, criteria).pipe(map(res => res.data));
	}

	searchContacts(criteria?: Partial<IGetUserContactsCriteria>): Observable<IUser[]> {
		const body = {
			query: String((criteria && criteria.userName) || '          '),
		};

		const url = this.common.getApiUrl(`mobile/users/search`, ApiVersion.v10);
		return this.http.post<IApiResponse<IUser[]>>(url, body).pipe(map(res => res.data));
	}

	getQuickReplies(): Observable<string[]> {
		// const url = this.common.getApiUrl(`users/current/quickreplies`, ApiVersion.v10);
		// return this.http.get<string[]>(url);
		this.quickRepliesReq$.next(0 as any);
		return this.quickReplies$;
	}

	getSessionUserSharedData<T = any>(propertyPath?: string): Observable<T> {
		const url = this.common.getApiUrl(`usersettings`);
		return this.http.get<IApiResponse<T>>(url, { params: { propertyPath } }).pipe(map(res => res.data));
	}

	setSessionUserSharedData<T = any>(value: any, propertyPath?: string): Observable<T> {
		const url = this.common.getApiUrl(`usersettings`);
		return this.http.post<any>(url, value, { params: { propertyPath } });
	}

	getCoworkers(data: ICowokersCriteria) {
		const url = this.common.getApiUrl(`user/coworkers`);
		return this.http.post<any>(url, data).pipe(map(res => res.data));
	}

	protected applyUserSettingsFromCookies(settings: IGetUserSettingsResult): IGetUserSettingsResult {
		settings = settings || ({} as any);
		let syndLayout = this.cookies.get('SyndLayout');
		const inPreview = this.cookies.get('TCPreview') === 'true';
		const commentsViewMode: any = this.cookies.get('CommentsViewMode');

		// in place not supported yet
		// if (!inPreview) {
		// 	settings.mtfPreview = MtfAreaView.inPlace;
		// } else
		if (!syndLayout) {
			syndLayout = 'horizontal';
			this.cookies.set('SyndLayout', syndLayout, undefined, '/');
		}
		if (syndLayout === 'vertical') {
			settings.mtfPreview = MtfAreaView.right;
		} else if (syndLayout === 'horizontal') {
			settings.mtfPreview = MtfAreaView.bottom;
		} else if (syndLayout === 'popup') {
			settings.mtfPreview = MtfAreaView.popup;
		}

		settings.commentsViewMode = commentsViewMode ?? 'down';

		// test
		// settings.mtfPreview = MtfAreaView.right;
		// settings.useNewTaskCard = true;
		// settings.autoReadCommentsInTask = false;
		// settings.commentsViewMode = 'down';

		return settings;
	}

	getUsersShortInfo(criteria: Partial<IUserShortInfo>) {
		const url = this.common.getApiUrl(`user/short-info`);
		return this.http.post<IApiResponse>(url, criteria).pipe(map(res => res.data));
	}

	getSignatures() {
		const url = this.common.getApiUrl(`user/signatures/tasks-for-accept`);
		return this.http.get<IApiResponse>(url).pipe(map(res => res.data));
	}
}
